import React, { Component, type ReactNode } from 'react';
import { toast } from 'react-hot-toast';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class QueryEditorErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error for debugging
    console.error('QueryEditor Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);
    
    // Show user-friendly error message
    toast.error('Something went wrong with the Query Editor. Please refresh the page.');
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI or default error message
      return this.props.fallback || (
        <div className="flex items-center justify-center h-full bg-white dark:bg-gray-800">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="mb-4">
              <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              The Query Editor encountered an unexpected error. This might be due to:
            </p>
            <ul className="text-sm text-gray-500 dark:text-gray-400 text-left mb-6 space-y-1">
              <li>• Connection issues with the database server</li>
              <li>• Invalid data format from the API</li>
              <li>• Network connectivity problems</li>
            </ul>
            <div className="space-y-2">
              <button type="button"
                onClick={() => { window.location.reload() }}
                className="w-full btn-primary"
              >
                Refresh Page
              </button>
              <button type="button"
                onClick={() => { this.setState({ hasError: false }) }}
                className="w-full btn-secondary"
              >
                Try Again
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default QueryEditorErrorBoundary;
